import "./scrollable.css";
import "./equipment-vehicles-page.scss";
import { Page } from "@nike/epic-react-ui";
import React, { useReducer, useRef, useMemo, useState, useCallback, useEffect } from "react";
import { Card, Text } from "@nike/eds";
import { GET_VEHICLES, LIST_VEHICLE_COUNTRIES, GET_VEHICLE, GET_VEHICLE_CONSIGNMENTS } from "../graphql/vehicles.graphql";
import { useQuery, useApolloClient } from '@apollo/client';
import type { VehicleTableColumn } from "../components/equipment-vehicles-table/EquipmentVehiclesTableColumns";
import { EquipmentVehiclesTable } from "../components/equipment-vehicles-table/EquipmentVehiclesTable";
import { getVehicleTableColumns } from "../components/equipment-vehicles-table/EquipmentVehiclesTableColumns";
import type { TableRef } from "../components/base-table";
import type { SortingState, ColumnFiltersState, ColumnOrderState, RowSelectionState, PaginationState, ExpandedState } from "@tanstack/react-table";
import { EquipmentVehicle, User } from "../graphql/graphql";
import { ConsignmentsTable } from '../components/consignments-table/ConsignmentsTable';
import { getConsignmentsTableColumns } from '../components/consignments-table/ConsignmentsTableColumns';
import { useParams, useSearchParams } from "react-router-dom";
import { encodeFilters, decodeFilters } from "../shared/Utils";
import { GqlErrorModal } from "../shared/GqlErrorModal";
import {equals} from "ramda";
import { UISettings, TableUISettings, DEFAULT_UI_SETTINGS } from "../shared/Types";
import { UserUISettingsService } from "../services/UserUISettingsService";

type EquipmentVehiclesPageState = {
  loading: boolean;
  error: any;
  data: EquipmentVehicle[];
  sorting: SortingState;
  columnOrder: ColumnOrderState;
  columnFilters: ColumnFiltersState;
  rowSelection: RowSelectionState;
  expanded?: ExpandedState;
  pagination: PaginationState & { totalCount?: number };
  globalSearch?: string;
  columnFiltersSidePanelIsOpen: boolean; 
  originCountryOptions: {value: string; label:string}[];
  dischargeCountryOptions: {value: string; label:string}[];
  viewingVehicle?: EquipmentVehicle;
  consignmentPagination: PaginationState & { totalCount?: number };
};

type EquipmentVehiclesPageAction =
  | { type: "FETCH_START" }
  | { type: "FETCH_SUCCESS"; payload: { items: EquipmentVehicle[]; totalCount?: number } }
  | { type: "FETCH_ERROR"; payload: any }
  | { type: "SET_SORTING"; payload: SortingState }
  | { type: "SET_COLUMN_ORDER"; payload: ColumnOrderState }
  | { type: "SET_COLUMN_FILTERS"; payload: ColumnFiltersState }
  | { type: "SET_ROW_SELECTION"; payload: RowSelectionState }
  | { type: "SET_VIEWING_VEHICLE"; payload: EquipmentVehicle | undefined }
  | { type: "SET_PAGINATION"; payload: PaginationState }
  | { type: "SET_EXPANDED"; payload: ExpandedState }
  | { type: "SET_GLOBAL_FILTER"; payload: string }
  | { type: "SET_ORIGIN_COUNTRY_OPTIONS"; payload: {value: string; label:string}[] }
  | { type: "SET_DISCHARGE_COUNTRY_OPTIONS"; payload: {value: string; label:string}[] }
  | { type: "OPEN_COLUMN_FILTERS_SIDE_PANEL" }    
  | { type: "CLOSE_COLUMN_FILTERS_SIDE_PANEL" }
  | { type: "SET_CONSIGNMENT_PAGINATION"; payload: PaginationState }

function equipmentVehiclesReducer(state: EquipmentVehiclesPageState, action: EquipmentVehiclesPageAction): EquipmentVehiclesPageState {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, loading: true, error: null };
    case "FETCH_SUCCESS":
      return { ...state, loading: false, error: null, data: action.payload.items, pagination: { ...state.pagination, totalCount: action.payload.totalCount } };
    case "FETCH_ERROR":
      return { ...state, loading: false, error: action.payload };
    case "SET_SORTING":
      return { ...state, sorting: action.payload };
    case "SET_COLUMN_ORDER":
      return { ...state, columnOrder: action.payload };
    case "SET_COLUMN_FILTERS": 
      return { 
        ...state, 
        columnFilters: [...action.payload],
        pagination: { 
          ...state.pagination, 
          pageIndex: 0 // reset to first page when filters change
        }
      };
    case "SET_ROW_SELECTION":
      return { ...state, rowSelection: action.payload };
    case "SET_VIEWING_VEHICLE":
      return { ...state, viewingVehicle: action.payload };
    case "SET_PAGINATION":
      return { ...state, pagination: { ...state.pagination, ...action.payload } };
    case "SET_GLOBAL_FILTER":
      return { ...state, globalSearch: action.payload };
    case "SET_EXPANDED":
      return { ...state, expanded: action.payload };
    case "OPEN_COLUMN_FILTERS_SIDE_PANEL":
      return { ...state, columnFiltersSidePanelIsOpen: true };
    case "CLOSE_COLUMN_FILTERS_SIDE_PANEL":
      return { ...state, columnFiltersSidePanelIsOpen: false };
    case "SET_ORIGIN_COUNTRY_OPTIONS":
      return { ...state, originCountryOptions: action.payload };
    case "SET_DISCHARGE_COUNTRY_OPTIONS":
      return { ...state, dischargeCountryOptions: action.payload };
    case "SET_CONSIGNMENT_PAGINATION":
      return { ...state, consignmentPagination: { ...state.consignmentPagination, ...action.payload } };
    default:
      return state;
  }
}

type EquipmentVehiclesProps = {
  history: any;
  currentTerritories: string[];
  currentUser?: User | undefined;
};

export function EquipmentVehicles({ history, currentTerritories, currentUser }: EquipmentVehiclesProps ) {
  const tableRef = useRef<TableRef>(null);
  const consignmentsTableRef = useRef<TableRef>(null);

  // Detect E2E test environment
  const isE2E = typeof window !== 'undefined' && (window as any).__E2E__ === true;

  const { id: vehicleIdFromUrl } = useParams<{ id: string }>();
  const mode = vehicleIdFromUrl ? "single" : "list";

  const [searchParams, setSearchParams] = useSearchParams();
  const [showErrorModal, setShowErrorModal] = useState(true);

  const initialColumnFilters = React.useMemo(
    () => decodeFilters(searchParams.get("filters")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams.get("filters")]
  );
  
  const initialGlobalSearch = React.useMemo(
    () => searchParams.get("tableSearch") || "",
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams.get("tableSearch")]
  );

  const client = useApolloClient?.();
  const settingsService = useMemo(() => new UserUISettingsService(client, isE2E), [client, isE2E]);
  const [settings, setSettings] = useState<UISettings | undefined>(undefined);

  const equipmentVehiclesSettingsKey = "equipmentVehicles";
  const consignmentSettingsKey = "consignments";

  const parsedUISettings: UISettings | undefined = (() => {
    const raw = currentUser?.settings?.uiSettings;
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw) as UISettings;
      } catch (e) {
        console.error("Failed to parse uiSettings:", raw, e);
        return undefined;
      }
    }
    return raw as UISettings | undefined;
  })();

  const equipmentVehicleTableSettings: TableUISettings | undefined = {
    pageSize: parsedUISettings?.[`${equipmentVehiclesSettingsKey}PageSize`] as number || 10,
    columnOrder: parsedUISettings?.[`${equipmentVehiclesSettingsKey}ColumnOrder`] as string[] || getVehicleTableColumns().map(col => col.accessorKey as string),
    columnVisibility: parsedUISettings?.[`${equipmentVehiclesSettingsKey}ColumnVisibility`] as Record<string, boolean> || {},
  };

  const consignmentTableSettings: TableUISettings | undefined = {
    pageSize: parsedUISettings?.[`${consignmentSettingsKey}PageSize`] as number || 10,
    columnOrder: parsedUISettings?.[`${consignmentSettingsKey}ColumnOrder`] as string[] || getVehicleTableColumns().map(col => col.accessorKey as string),
    columnVisibility: parsedUISettings?.[`${consignmentSettingsKey}ColumnVisibility`] as Record<string, boolean> || {},
  };

  useEffect(() => {
      settingsService.getSettings().then(setSettings);
  }, [settingsService]);

  const [state, dispatch] = useReducer(equipmentVehiclesReducer, {
    loading: false,
    error: null,
    data: [],
    sorting: [],
    columnOrder: getVehicleTableColumns().map(col => col.accessorKey as string),
    columnFilters: initialColumnFilters,
    rowSelection: {},
    pagination: { pageIndex: 0, pageSize: equipmentVehicleTableSettings?.pageSize || 10, totalCount: undefined },
    globalSearch: initialGlobalSearch,
    expanded: {} as ExpandedState,
    columnFiltersSidePanelIsOpen: false,
    originCountryOptions: [],
    dischargeCountryOptions: [],
    viewingVehicle: undefined,
    consignmentPagination: { pageIndex: 0, pageSize: consignmentTableSettings?.pageSize || 10, totalCount: undefined },
  });

  // Read URL -> state (only when the raw query string pieces change)
  React.useEffect(() => {
    const filtersParam = searchParams.get("filters") || "";
    const tableSearchParam = searchParams.get("tableSearch") || "";

    const decoded = decodeFilters(filtersParam);
    if (!equals(decoded, state.columnFilters)) {
      dispatch({ type: "SET_COLUMN_FILTERS", payload: decoded });
    }

    if ((state.globalSearch || "") !== tableSearchParam) {
      dispatch({ type: "SET_GLOBAL_FILTER", payload: tableSearchParam });
    }
    // depend only on the primitive values you read
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("filters"), searchParams.get("tableSearch")]);

  // State -> URL (only when state changes; clone params, do not mutate original)
  React.useEffect(() => {
    const currentFiltersParam = searchParams.get("filters") || "";
    const currentSearchParam = searchParams.get("tableSearch") || "";

    const encodedFilters = encodeFilters(state.columnFilters);
    let shouldUpdate = false;

    const next = new URLSearchParams(searchParams); // clone

    if (encodedFilters !== currentFiltersParam) {
      if (encodedFilters) next.set("filters", encodedFilters);
      else next.delete("filters");
      shouldUpdate = true;
    }

    const desiredSearch = state.globalSearch || "";
    if (desiredSearch !== currentSearchParam) {
      if (desiredSearch) next.set("tableSearch", desiredSearch);
      else next.delete("tableSearch");
      shouldUpdate = true;
    }

    if (shouldUpdate) {
      setSearchParams(next, { replace: true });
    }
    // Only depend on state pieces we mirror + the primitive params we compare against
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.columnFilters, state.globalSearch]);
  
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.key === "Escape") && state.columnFiltersSidePanelIsOpen) {
        dispatch({ type: "CLOSE_COLUMN_FILTERS_SIDE_PANEL" });
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [state.columnFiltersSidePanelIsOpen]);
  
  const shouldSkipQuery = !currentTerritories || currentTerritories.length === 0 || !currentUser;

const { data: originCountriesData } = useQuery(LIST_VEHICLE_COUNTRIES, {
    skip: shouldSkipQuery,
    variables: {
      fieldType: "origin",
      customsTerritories: currentTerritories,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (originCountriesData?.equipmentVehicle?.listCountries) {
      const countries = originCountriesData.equipmentVehicle.listCountries;
      const newOptions = countries.map((c: string) => ({ value: c, label: c }));
      if (!equals(state.originCountryOptions, newOptions)) {
        dispatch({ type: "SET_ORIGIN_COUNTRY_OPTIONS", payload: newOptions });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originCountriesData]);

  // Discharge countries
  const { data: dischargeCountriesData } = useQuery(LIST_VEHICLE_COUNTRIES, {
    skip: shouldSkipQuery,
    variables: {
      fieldType: "discharge",
      customsTerritories: currentTerritories,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (dischargeCountriesData?.equipmentVehicle?.listCountries) {
      const countries = dischargeCountriesData.equipmentVehicle.listCountries;
      const newOptions = countries.map((c: string) => ({ value: c, label: c }));
      if (!equals(state.dischargeCountryOptions, newOptions)) {
        dispatch({ type: "SET_DISCHARGE_COUNTRY_OPTIONS", payload: newOptions });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dischargeCountriesData]);

  function getOrderByFromSorting(
    sorting: { id: string; desc: boolean }[],
    columns: ReturnType<typeof getVehicleTableColumns>
  ) {
    if (!sorting.length) return undefined;
    const sort = sorting[0];
    const col = columns.find(c => c.accessorKey === sort.id);
    if (!col || !col.sortableField) return undefined;
    return {
      field: col.sortableField,
      direction: sort.desc ? "DESC" : "ASC",
    };
  }

  const columnsDef = useMemo(() => getVehicleTableColumns(), []);
  const orderBy = useMemo(() => getOrderByFromSorting(state.sorting, columnsDef), [state.sorting, columnsDef]);

  function columnFiltersToQueryFilter(columnFilters: ColumnFiltersState, columns: VehicleTableColumn<EquipmentVehicle>[], globalSearch?: string): Record<string, any> | null {
    const filter: Record<string, any> = {};
    columnFilters.forEach(({ id, value }) => {
      // Find the columnDef for this filter
      const col = columns.find(c => c.accessorKey === id || c.id === id);
      const filterKey = col?.filterKey || id;

    if (
      typeof value === 'object' &&
      value !== null &&
      (Object.prototype.hasOwnProperty.call(value, 'from') || Object.prototype.hasOwnProperty.call(value, 'to'))
    ) {
        const from = (value as { from?: string }).from;
        const to = (value as { to?: string }).to;
        filter[`${filterKey}From`] = from || undefined;
        filter[`${filterKey}To`] = to || undefined;
      } else {
        filter[filterKey] = value;
      }
    });

    if (globalSearch) {
      filter.searchTerm = globalSearch;
    }
    const hasValidFilter = Object.values(filter).some(
      v => v !== undefined && v !== null && v !== ''
    );

    return hasValidFilter ? filter : null;
  }
  
  const queryFilter = React.useMemo(() => columnFiltersToQueryFilter(state.columnFilters, columnsDef, state.globalSearch),
      [state.columnFilters, columnsDef, state.globalSearch]
  );

  // Use GET_VEHICLE if vehicleIdFromUrl is present, otherwise GET_VEHICLES
  const singleVehicleQuery = mode === "single" && !!vehicleIdFromUrl;

  const vehiclesVariables = React.useMemo(() => {
    if (singleVehicleQuery) {
      return { id: vehicleIdFromUrl };
    }
    return {
      limit: state.pagination.pageSize,
      offset: state.pagination.pageIndex * state.pagination.pageSize,
      filter: queryFilter,
      orderBy: orderBy,
      customsTerritories: currentTerritories
    };
  }, [
    singleVehicleQuery,
    vehicleIdFromUrl,
    state.pagination.pageSize,
    state.pagination.pageIndex,
    queryFilter,
    orderBy,
    currentTerritories,
  ]);

  const { loading, error, data } = useQuery(
    singleVehicleQuery ? GET_VEHICLE : GET_VEHICLES,
    {
      skip: shouldSkipQuery,
      variables: vehiclesVariables,
      fetchPolicy: isE2E ? 'cache-first' : 'network-only',
    }
  );

  useEffect(() => {
    if (!data) return;
    let items: EquipmentVehicle[] = [];
    let totalCount = 1;
    if (singleVehicleQuery) {
      const vehicle = data?.equipmentVehicle?.get;
      if (!vehicle) {
        dispatch({
          type: "FETCH_ERROR",
          payload: { message: "Vehicle not found or does not exist." }
        });
        return;
      }
      items = [vehicle];
    } else {
      items = data?.equipmentVehicle?.list?.items || [];
      totalCount = data?.equipmentVehicle?.list?.pageInfo?.totalCount;
    }
    dispatch({ type: "FETCH_SUCCESS", payload: { items, totalCount } });
  }, [data, singleVehicleQuery]);

  useEffect(() => {
    if (error) {
      dispatch({ type: "FETCH_ERROR", payload: error });
    }
  }, [error]);

  useEffect(() => {
    if (loading) dispatch({ type: "FETCH_START" });
  }, [loading]);

  const columns = useMemo(() => getVehicleTableColumns(), []);

  const expandedContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (state.expanded && Object.values(state.expanded).some(Boolean)) {
      expandedContentRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [state.expanded]);

  type ExpandedConsignmentsProps = {
    vehicle: EquipmentVehicle;
    consignmentPageIndex: number;
    consignmentPageSize: number;
    onPaginationChange: (pagination: PaginationState) => void;
    consignmentsTableRef: React.RefObject<TableRef>;
  };

  function ExpandedConsignments({
    vehicle,
    consignmentPageIndex,
    consignmentPageSize,
    onPaginationChange,
    consignmentsTableRef,
  }: ExpandedConsignmentsProps) {

    const { data, loading, error } = useQuery(GET_VEHICLE_CONSIGNMENTS, {
      variables: {
        vehicleId: vehicle.id,
        limit: consignmentPageSize,
        offset: consignmentPageIndex * consignmentPageSize,
      },
      fetchPolicy: "cache-first",
    });

    const consignments = data?.equipmentVehicle?.get?.consignments?.items ?? [];
    const totalCount = data?.equipmentVehicle?.get?.consignments?.pageInfo?.totalCount ?? 0;

    return (
      <ConsignmentsTable
        tableRef={consignmentsTableRef}
        parentTableRef={tableRef} // Pass the parent table ref
        columns={getConsignmentsTableColumns}
        data={consignments}
        isLoading={loading}
        error={error}
        state={{
          sorting: [],
          columnFilters: [],
          setSorting: () => {},
          setColumnFilters: () => {},
          columnOrder: [],
          setColumnOrder: () => {},
          setRowSelection: () => {},
          rowSelection: {},
          resetRowSelection: () => {},
          columnVisibility: {},
          setColumnVisibility: () => {},
          pagination: { totalCount },
          globalSearch: "",
          expanded: {},
          columnFiltersSidePanelIsOpen: false,
        }}
        enableGlobalFilter={false}
        enableRowSelection={false}
        meta={{}}
        pageIndex={consignmentPageIndex}
        pageSize={consignmentPageSize}
        totalCount={totalCount}
        onPaginationChange={onPaginationChange}
        enablePagination={true}
      />
    );
  }

  const renderExpandedContent = (vehicle: EquipmentVehicle) => {
    const consignmentPageIndex = state.consignmentPagination.pageIndex;
    const consignmentPageSize = state.consignmentPagination.pageSize;

    return (
      <div ref={expandedContentRef}>
      <ExpandedConsignments
        vehicle={vehicle}
        consignmentPageIndex={consignmentPageIndex}
        consignmentPageSize={consignmentPageSize}
        onPaginationChange={(pagination) => {
          dispatch({ type: "SET_CONSIGNMENT_PAGINATION", payload: pagination });
          const newSettings = settingsService.setTablePageSize(
            settings || { consignmentsBannerDismissed: "false" },
            consignmentSettingsKey,
            pagination.pageSize
          );
          settingsService.changeSettings(newSettings).then(setSettings);
        }}
        consignmentsTableRef={consignmentsTableRef}
      />
      </div>
    );
  };

  // Select and expand the row if the id is in the URL and data is loaded
  React.useEffect(() => {
    if (
      vehicleIdFromUrl &&
      state.data.length > 0 &&
      !state.expanded?.[vehicleIdFromUrl]
    ) {
      const selectedVehicle = state.data.find((v) => v.id === vehicleIdFromUrl);
      if (selectedVehicle) {
        dispatch({ type: "SET_ROW_SELECTION", payload: { [vehicleIdFromUrl]: true } });
        dispatch({ type: "SET_VIEWING_VEHICLE", payload: selectedVehicle });
        dispatch({ type: "SET_EXPANDED", payload: { [vehicleIdFromUrl]: true } });
      }
    }
  }, [vehicleIdFromUrl, state.data, state.expanded]); // re-run when URL or data changes

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__EQUIPMENT_VEHICLES_READY__ = true;
    }
  }, [state.data]);

  const setSorting = useCallback((newSorting: SortingState) => 
    dispatch({ type: "SET_SORTING", payload: newSorting }), []);

  const setColumnFilters = useCallback((newFilters: ColumnFiltersState) => 
    dispatch({ type: "SET_COLUMN_FILTERS", payload: newFilters }), []);

  return (
    <Page
      documentTitle={
        mode === "single"
          ? `Transport Vehicle - Customs Compliance Hub`
          : "Transport Vehicles - Customs Compliance Hub"
      }
      width="full"
      className={`scrollable equipment-vehicles-page ${mode === "single" ? "single-vehicle" : "vehicle-list"}`}
    >

      { mode === "single" && state.error ? (
        <Card >
          <Text className="eds-alert eds-flex--justify-content-center" as="h4" font="title-4">Vehicle Not Found</Text>
        </Card>
      ) : (
        <EquipmentVehiclesTable
          tableRef={tableRef}
          columns={() => columns}
          data={state.loading ? [] : state.data}
          tableTitle={<Text font="title-2" as="h2">{mode === "single" ? "Transport Vehicle" : "Transport Vehicles"}</Text>}
          getRowId={(row: EquipmentVehicle) => row.id ?? ""}
          state={{
            sorting: state.sorting,
            columnFilters: state.columnFilters,
            setSorting: setSorting,
            setColumnFilters: setColumnFilters,
            columnOrder: state.columnOrder,
            setColumnOrder: (newOrder: string[]) => dispatch({ type: "SET_COLUMN_ORDER", payload: newOrder }),
            setRowSelection: (newRowSelection: RowSelectionState) => {
              const rowId = Object.keys(newRowSelection)[0];
              const isSelected = !!state.rowSelection[rowId];
              const isExpanded = !!state.expanded?.[rowId];

              if (isSelected && isExpanded) {
                // Deselect and collapse
                dispatch({ type: "SET_ROW_SELECTION", payload: {} });
                dispatch({ type: "SET_VIEWING_VEHICLE", payload: undefined });
                dispatch({ type: "SET_EXPANDED", payload: {} });
              } else {
                // Select and expand
                const selectedVehicle = state.data.find((v) => v.id === rowId);
                dispatch({ type: "SET_ROW_SELECTION", payload: { [rowId]: true } });
                dispatch({ type: "SET_VIEWING_VEHICLE", payload: selectedVehicle });
                dispatch({ type: "SET_EXPANDED", payload: { [rowId]: true } });
              }
            },
            rowSelection: state.rowSelection,
            resetRowSelection: () => dispatch({ type: "SET_ROW_SELECTION", payload: {} }),
            pagination: { totalCount: state.pagination.totalCount },
            globalSearch: state.globalSearch,
            expanded: state.expanded,
            columnFiltersSidePanelIsOpen: state.columnFiltersSidePanelIsOpen, 
          }}
          onPaginationChange={mode === "single" ? undefined : (pagination) => {
            dispatch({ type: "SET_PAGINATION", payload: pagination });
            const newSettings = settingsService.setTablePageSize(
              settings || DEFAULT_UI_SETTINGS,
              equipmentVehiclesSettingsKey,
              pagination.pageSize
            );
            settingsService.changeSettings(newSettings).then(setSettings);
          }}
          pageIndex={state.pagination.pageIndex}
          pageSize={state.pagination.pageSize}
          totalCount={state.pagination.totalCount}
          isLoading={state.loading}
          error={state.error}
          enableGlobalFilter={mode !== "single"}
          onGlobalFilterChange={mode === "single" ? undefined : (filter) => dispatch({ type: "SET_GLOBAL_FILTER", payload: filter })}
          onExpandedChange={(expanded: ExpandedState) => {
            dispatch({ type: "SET_EXPANDED", payload: expanded })
          }}
          onColumnFiltersSidePanelOpenChange={mode === "single" ? undefined : (isOpen) => {
            if (isOpen) {
              dispatch({ type: "OPEN_COLUMN_FILTERS_SIDE_PANEL" });
            } else {
              dispatch({ type: "CLOSE_COLUMN_FILTERS_SIDE_PANEL" });
            }
          }}
          enableColumnFiltersSidePanel={mode !== "single"}
          meta={{
            originCountryOptions: state.originCountryOptions,
            dischargeCountryOptions: state.dischargeCountryOptions,
          }}
          renderExpandedContent={renderExpandedContent}
          enablePagination={mode !== "single"}
        />
      )}
      {showErrorModal && error && (
        <GqlErrorModal error={error} onClose={() => setShowErrorModal(false)} />
      )}
    </Page>
  );
}
